import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] p-8 text-center">
          <div className="bg-error/10 p-4 rounded-full mb-4">
            <AlertTriangle className="text-error" size={32} />
          </div>
          <h2 className="text-xl font-semibold text-on-surface mb-2">
            Algo deu errado
          </h2>
          <p className="text-on-surface-variant text-sm mb-6 max-w-md">
            Ocorreu um erro inesperado. Tente recarregar a página ou voltar à tela anterior.
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <RefreshCw size={16} />
              Tentar novamente
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-surface-container text-on-surface rounded-lg text-sm font-medium border border-outline-variant/20 hover:bg-surface-container-high transition-colors"
            >
              Ir para o início
            </button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-6 text-left w-full max-w-lg">
              <summary className="text-xs text-on-surface-variant cursor-pointer">
                Detalhes do erro (dev)
              </summary>
              <pre className="mt-2 p-3 bg-surface-container rounded-lg text-xs text-error overflow-auto max-h-48">
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
